import type { CacheItem } from './schema/CacheItem';
import type { CacheDescriptor } from './schema/CacheDescriptor';

/**
 * We don't want to put functions in the store, because they do not change, but we want to allow the items-cache
 * consumer to initialize a function for filtering items via descriptor. These cachedFunctions are maintained here.
 */
let cacheFunctions: { [key: string]: ItemCacheFunctions<any, any> } = {};

export interface ItemCacheFunctions<T extends CacheItem, U extends CacheDescriptor> {
    /**
     * Filter function to determine if item matches the descriptor. This is used to get
     * items matching a descriptor from the cache.
     *
     * @type {(descriptor: U) => (item: T)}
     */
    buildItemDescriptorFilter: (descriptor: U) => (item: T) => boolean;

    /**
     * Checks if descriptor1 constains descriptor2, descriptor1 contains descriptor2 if all the elements represented by descriptor2
     * are also represented by descriptor1.
     *
     * This is used to determine if a descriptor is a subset of a descriptor that is already in the cache. So, it helps us
     * determine if the items we are getting are already in the cache. The descriptorId is also used for this purpose, but it only allows us to identify situations where the exact same descriptor
     * has been added to the cache. This function allows us to identify situations where a descriptor is a subset of another descriptor in the cache.
     *
     * @type {(descriptor1: U, descriptor2: U) => boolean}
     */
    containsDescriptor: (descriptor1: U, descriptor2: U) => boolean;
}

export function getCachedFunctions<T extends CacheItem, U extends CacheDescriptor>(
    key: string
): ItemCacheFunctions<T, U> {
    return cacheFunctions[key];
}

export function addCachedFunctions<T extends CacheItem, U extends CacheDescriptor>(
    key: string,
    functions: ItemCacheFunctions<T, U>
): ItemCacheFunctions<T, U> {
    cacheFunctions[key] = functions;
    return functions;
}
